import { ChangeEvent, useState } from 'react';
import {
    Dialog,
    Box,
    Typography,
    TextField,
    Button,
    Chip,
    Avatar,
} from '@mui/material';
import { CheckCircle, Cancel, FileUpload, PictureAsPdf, Description } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { EPeriodicStatus } from '../../models/periodic/periodic';

const MAX_LENGTH_CHAR = 2000;

interface ActionDialogProps {
    open: boolean;
    onClose: () => void;
    actionStatus: EPeriodicStatus | null;
    onSubmit: (comment: string, files: File[]) => Promise<void>;
}

export const ActionDialog = ({ open, onClose, actionStatus, onSubmit }: ActionDialogProps) => {
    const { t } = useTranslation(['periodical', 'common']);
    const [actionComment, setActionComment] = useState('');
    const [actionFiles, setActionFiles] = useState<File[]>([]);

    const handleSubmit = async () => {
        await onSubmit(actionComment, actionFiles);
        setActionComment('');
        setActionFiles([]);
        onClose();
    };

    const onFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) return;

        const filesArray = Array.from(event.target.files);
        const MAX_SIZE = 25 * 1024 * 1024;

        const filteredFiles = filesArray.filter(file => {
            const isExe = file.name.toLowerCase().endsWith('.exe');
            const isTooLarge = file.size > MAX_SIZE;
            const isValidType =
                file.type.includes('image') ||
                file.type === 'application/pdf' ||
                file.type === 'text/plain';

            const isInvalid = isExe || isTooLarge || !isValidType;

            if (isTooLarge) {
                alert(`${file.name} ${t('info.fileTooLarge')} (Max 25MB)`);
            }

            return !isInvalid;
        });

        setActionFiles(prev => [...prev, ...filteredFiles]);
    };


    const removeFile = (index: number) => {
        setActionFiles(prev => prev.filter((_, idx) => idx !== index));
    };

    const isAccepted = actionStatus === EPeriodicStatus.Accepted;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Box sx={{
                        width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        bgcolor: isAccepted ? '#e8f5e9' : '#ffebee',
                        color: isAccepted ? '#2e7d32' : '#d32f2f'
                    }}>
                        {isAccepted ? <CheckCircle fontSize="large" /> : <Cancel fontSize="large" />}
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                            {isAccepted ? t('button.validateTask') : t('button.rejectTask')}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            {t('validation.commentOptional')}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: actionComment.length >= MAX_LENGTH_CHAR ? 'error.main' : 'text.secondary' }}>
                            {actionComment.length} / {MAX_LENGTH_CHAR}
                        </Typography>
                    </Box>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder={isAccepted ? t('info.placeholderAccepted') : t('info.placeholderRefused')}
                        value={actionComment}
                        onChange={(e) => setActionComment(e.target.value)}
                        inputProps={{ maxLength: MAX_LENGTH_CHAR }}
                        error={actionComment.length >= MAX_LENGTH_CHAR}
                        helperText={actionComment.length >= MAX_LENGTH_CHAR ? t('validation.maxCharactersReached') : ''}
                    />
                </Box>

                <Typography variant="caption" sx={{ fontWeight: 700, mb: 1, display: 'block' }}>
                    {t('info.attachEvidence')}
                </Typography>
                <Box sx={{ border: '2px dashed #e0e0e0', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fafafa', cursor: 'pointer', '&:hover': { bgcolor: '#f0f4f8', borderColor: '#2196f3' } }}>
                    <input
                        type="file"
                        multiple
                        accept="image/*,application/pdf,text/plain"
                        style={{ display: 'none' }}
                        id="manager-upload-input"
                        onChange={onFileUpload}
                    />
                    <label htmlFor="manager-upload-input" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                        <FileUpload sx={{ color: '#bdbdbd', fontSize: 40, mb: 1 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            {t('validation.clickUploadFiles')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            {t('info.supportedFormats', { size: '25MB' })}
                        </Typography>
                    </label>
                </Box>

                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {actionFiles.map((file, i) => {
                        let fileAvatar;
                        if (file.type.includes('image')) {
                            fileAvatar = <Avatar src={URL.createObjectURL(file)} />;
                        } else if (file.type === 'application/pdf') {
                            fileAvatar = <Avatar sx={{ bgcolor: '#f44336' }}><PictureAsPdf fontSize="small" /></Avatar>;
                        } else {
                            fileAvatar = <Avatar sx={{ bgcolor: '#757575' }}><Description fontSize="small" /></Avatar>;
                        }

                        return (
                            <Chip
                                key={i}
                                label={`${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`}
                                size="small"
                                onDelete={() => removeFile(i)}
                                avatar={fileAvatar}
                            />
                        );
                    })}
                </Box>

                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button onClick={onClose} color="inherit">{t('button.cancel')}</Button>
                    <Button variant="contained" color={isAccepted ? 'success' : 'error'} onClick={handleSubmit}>
                        {t('validation.confirmation')}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};